import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  styled,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  Regulation,
  RegulationsResponse,
  getRegulations,
} from "@services/regulations";

const RegulationsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "12px",
  gridAutoRows: "minmax(160px, auto)",
  width: "100%",
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: "repeat(1, 1fr)",
  },
}));

const RegulationCard = styled(Box)({
  backgroundColor: "#ffffff",
  border: "1px solid #0A829D",
  borderRadius: "4px",
  boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.05)",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  minHeight: "180px",
  "&:hover": {
    boxShadow: "0px 8px 16px 0px rgba(0,0,0,0.1)",
    transform: "translateY(-2px)",
    transition: "all 0.2s ease-in-out",
    backgroundColor: "#ECFCFF",
  },
});

const CardContent = styled(Box)(({ theme }) => ({
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  flex: 1,
}));

const CardTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  color: "#065b70",
  marginBottom: "8px",
});

const CardDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#4f4f4f",
  marginBottom: "12px",
  lineHeight: 1.4,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  height: "91px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "auto",
}));

const LogoPlaceholder = styled(Box)(({ theme }) => ({
  width: "91px",
  height: "91px",
  //backgroundColor: "#f5f5f5",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "8px",
  },
}));

const ActionButton = styled(Box)({
  position: "absolute",
  bottom: "0",
  right: "0",
  backgroundColor: "#3095ab",
  border: "1px solid #086e86",
  borderTopLeftRadius: "8px",
  padding: "8px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#086e86",
  },
});

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  flexDirection: "column",
  gap: "16px",
});

const ErrorContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  padding: "20px",
  textAlign: "center",
});

const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="#ffffff">
    <path d="M7 7h8.586L5.293 17.293l1.414 1.414L17 8.414V17h2V5H7v2z" />
  </svg>
);

interface RegulationsTabProps {
  onRegulationClick?: (regulation: Regulation) => void;
  facilityTypeSlug?: string;
  countryCode?: string;
}

export default function RegulationsTab({
  onRegulationClick,
  facilityTypeSlug,
  countryCode,
}: RegulationsTabProps) {
  const [regulationsData, setRegulationsData] = useState<RegulationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRegulationsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getRegulations({
        facilityTypeSlug,
        countryCode,
      });
      setRegulationsData(response);
    } catch (err) {
      console.error("Failed to load regulations data:", err);
      setError(err instanceof Error ? err.message : "Failed to load regulations data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegulationsData();
  }, [facilityTypeSlug, countryCode]);

  const handleRegulationClick = (regulation: Regulation) => {
    if (onRegulationClick) {
      onRegulationClick(regulation);
    }
    console.log("Clicked regulation:", regulation.name);
  };

  const handleRetry = () => {
    loadRegulationsData();
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} sx={{ color: "#0a829d" }} />
        <Typography sx={{ color: "#737373" }}>Loading regulations...</Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
          Failed to Load Regulations Data
        </Typography>
        <Typography variant="body2" sx={{ color: "#737373", mb: 2 }}>
          {error}
        </Typography>
        <Button onClick={handleRetry} variant="contained" sx={{ backgroundColor: "#0a829d" }}>
          Retry
        </Button>
      </ErrorContainer>
    );
  }

  if (!regulationsData) {
    return (
      <Box>
        <Typography>No regulations data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <RegulationsGrid>
        {regulationsData.regulations.map((regulation) => (
          <RegulationCard
            key={regulation.id}
            onClick={() => handleRegulationClick(regulation)}
          >
            <CardContent>
              <CardTitle>{regulation.name}</CardTitle>
              <CardDescription>{regulation.description}</CardDescription>
              <LogoContainer>
                <LogoPlaceholder>
                {regulation.logo_url ? (
                  <img 
                    src={regulation.logo_url} 
                    alt={`${regulation.name} logo`}
                    onError={(e) => {
                      // Fallback to emoji if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.textContent = regulation.logo_emoji;
                      }
                    }}
                  />
                ) : (
                  regulation.logo_emoji
                )}
                </LogoPlaceholder>
              </LogoContainer>
            </CardContent>
            <ActionButton>
              <ArrowIcon />
            </ActionButton>
          </RegulationCard>
        ))}
      </RegulationsGrid>
    </Box>
  );
}